import React, { useEffect, useMemo, useState, useRef } from 'react';
import styles from './ProgramCalendar.module.scss';
import { CalendarWeek } from './CalendarWeek';
import { Day } from '../../constants';
import { buildSlotsForWeek } from '../../utils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';

import type { AssignedProgram, WithOptionalId } from '../../types';
import type { DraggedWorkoutLocation } from './CalendarWeek';

interface ProgramCalendarProps {
  program: WithOptionalId<AssignedProgram>;
  onAddWorkout: (weekIndex: number, day: Day) => void;
  onEditWorkout: (weekIndex: number, day: Day) => void;
  onRemoveWorkout: (weekIndex: number, day: Day) => void;
  setSelectedWeekIndex: (weekIndex: number) => void;
  onDuplicateWeek: (weekIndex: number) => void;
  onDeleteWeek: (weekIndex: number) => void;
  weeksDates: Date[][];
  onMoveWorkout?: (
    fromWeekIndex: number,
    fromDay: Day,
    toWeekIndex: number,
    toDay: Day,
  ) => void;
  draggingWorkout?: DraggedWorkoutLocation | null;
  setDraggingWorkout?: (value: DraggedWorkoutLocation | null) => void;
}

export const ProgramCalendar: React.FC<ProgramCalendarProps> = ({
  program,
  onAddWorkout,
  onEditWorkout,
  onRemoveWorkout,
  setSelectedWeekIndex,
  onDuplicateWeek,
  onDeleteWeek,
  weeksDates,
  onMoveWorkout,
  draggingWorkout,
  setDraggingWorkout,
}) => {
  // Build month groupings using the Sunday of each week as the representative date
  const months = useMemo(() => {
    const groups: Array<{
      month: number;
      year: number;
      startWeekIndex: number;
      label: string;
    }> = [];
    if (!weeksDates || weeksDates.length === 0) return groups;
    // Build month groups for all available weeksDates (may exceed program items)
    for (let i = 0; i < weeksDates.length; i += 1) {
      const week = weeksDates[i];
      if (!week || week.length === 0) continue;
      const start = week[0];
      const month = start.getMonth();
      const year = start.getFullYear();
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.month !== month || lastGroup.year !== year) {
        const label = `${start.toLocaleString('default', {
          month: 'long',
        })} ${year}`;
        groups.push({ month, year, startWeekIndex: i, label });
      }
    }
    return groups;
  }, [weeksDates]);

  // Determine current week index (the week containing today)
  const currentWeekIndex = useMemo(() => {
    if (!weeksDates || weeksDates.length === 0) return 0;
    const today = new Date();
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    let foundIdx = -1;
    for (let i = 0; i < weeksDates.length; i += 1) {
      const week = weeksDates[i];
      if (!week || week.length < 7) continue;
      const start = new Date(
        week[0].getFullYear(),
        week[0].getMonth(),
        week[0].getDate(),
      );
      const end = new Date(
        week[6].getFullYear(),
        week[6].getMonth(),
        week[6].getDate(),
      );
      if (todayDate >= start && todayDate <= end) {
        foundIdx = i;
        break;
      }
    }
    if (foundIdx === -1) {
      // If today is before the first week
      const firstWeek = weeksDates[0];
      const lastWeek = weeksDates[Math.max(0, weeksDates.length - 1)];
      if (
        firstWeek &&
        todayDate <
        new Date(
          firstWeek[0].getFullYear(),
          firstWeek[0].getMonth(),
          firstWeek[0].getDate(),
        )
      ) {
        return 0;
      }
      if (lastWeek) return Math.max(0, weeksDates.length - 1);
      return 0;
    }
    return foundIdx;
  }, [weeksDates]);

  // Selected month index (for navigation)
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  // Initialize selected month to the month containing the current week
  useEffect(() => {
    if (months.length === 0) return;
    const idx = months.reduce(
      (acc, m, i) => (m.startWeekIndex <= currentWeekIndex ? i : acc),
      0,
    );
    setSelectedMonthIndex(idx);
  }, [months, currentWeekIndex]);

  const isScrollingProgrammatically = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToWeek = (weekIndex: number) => {
    const el = document.getElementById(`week-${weekIndex}`);
    if (el) {
      isScrollingProgrammatically.current = true;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 600);
    }
  };

  // Auto scroll to the current week on mount/when weeksDates change
  useEffect(() => {
    if (!weeksDates || weeksDates.length === 0) return;
    scrollToWeek(currentWeekIndex);
  }, [weeksDates, currentWeekIndex]);

  useEffect(() => {
    if (!weeksDates || weeksDates.length === 0 || months.length === 0) return;

    const scrollContainer = document.querySelector(`.${styles.content}`);
    if (!scrollContainer) return;

    const computeAndSetMonth = () => {
      if (isScrollingProgrammatically.current) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const referenceY = containerRect.top + containerRect.height * 0.25;

      let bestWeekIndex = -1;
      let bestDistance = Infinity;

      for (let i = 0; i < weeksDates.length; i++) {
        const el = document.getElementById(`week-${i}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Skip weeks fully outside the viewport
        if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
          continue;
        }
        const distance = Math.abs(rect.top - referenceY);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestWeekIndex = i;
        }
      }

      if (bestWeekIndex === -1) return;
      const monthIdx = months.reduce(
        (acc, m, i) => (m.startWeekIndex <= bestWeekIndex ? i : acc),
        0,
      );
      setSelectedMonthIndex((prev) => (prev === monthIdx ? prev : monthIdx));
    };

    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        computeAndSetMonth();
      });
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount/data-change to sync initial state
    computeAndSetMonth();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, [weeksDates, months, styles.content]);

  const hasPrev = selectedMonthIndex > 0;
  const hasNext = selectedMonthIndex < Math.max(0, months.length - 1);

  const goToPreviousMonth = (): void => {
    if (!hasPrev) return;
    const newIdx = Math.max(0, selectedMonthIndex - 1);
    setSelectedMonthIndex(newIdx);
    const weekIdx = months[newIdx]?.startWeekIndex ?? 0;
    setSelectedWeekIndex(weekIdx);
    scrollToWeek(weekIdx);
  };

  const goToNextMonth = (): void => {
    if (!hasNext) return;
    const newIdx = Math.min(months.length - 1, selectedMonthIndex + 1);
    setSelectedMonthIndex(newIdx);
    const weekIdx = months[newIdx]?.startWeekIndex ?? 0;
    setSelectedWeekIndex(weekIdx);
    scrollToWeek(weekIdx);
  };

  const renderMonthHeaderIfNeeded = (weekIndex: number) => {
    const monthEntry = months.find((m) => m.startWeekIndex === weekIndex);
    if (!monthEntry) return null;
    return <div className={styles.monthHeader}>{monthEntry.label}</div>;
  };

  const renderWeek = (weekIndex: number) => {
    return (
      <div
        className={styles.weekWrapper}
        key={`week-${weekIndex}`}
        id={`week-${weekIndex}`}
      >
        <div className={styles.weekContainer}>
          <CalendarWeek
            weekIndex={weekIndex}
            days={buildSlotsForWeek(weekIndex, program, weeksDates)}
            onAddWorkout={(w, d) => onAddWorkout(w, d)}
            onEditWorkout={(w, d) => onEditWorkout(w, d)}
            onRemoveProgramItem={(w, d) => onRemoveWorkout(w, d)}
            onDuplicateWeek={(w) => onDuplicateWeek(w)}
            onDeleteWeek={(w) => onDeleteWeek(w)}
            onMoveWorkout={onMoveWorkout}
            draggingWorkout={draggingWorkout}
            setDraggingWorkout={setDraggingWorkout}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton
          className={styles.navButton}
          onClick={goToPreviousMonth}
          disabled={!hasPrev}
          aria-label="Previous month"
        >
          <ChevronLeftIcon color="primary" />
        </IconButton>
        <div className={styles.monthLabel}>
          {months[selectedMonthIndex]?.label ?? ''}
        </div>
        <IconButton
          className={styles.navButton}
          onClick={goToNextMonth}
          disabled={!hasNext}
          aria-label="Next month"
        >
          <ChevronRightIcon color="primary" />
        </IconButton>
      </div>

      <div className={styles.content}>
        {weeksDates.map((_, index) => (
          <React.Fragment key={`week-frag-${index}`}>
            {renderMonthHeaderIfNeeded(index)}
            {renderWeek(index)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
